// RaceChart.js — match-day-by-match-day lines for the chosen clubs and measure, with
// a crest at the head of each line, a live leaderboard, and play / step /
// scrub / speed controls. Drawn as SVG sized to its container. Data comes
// from raceFor() in league.js; each club keeps the colour it was given.
//
// Two views: "Follow" (default) shows a trailing window of about a season
// and a half with the value axis fitted to what's on screen, so clubs stay
// apart and overtakes are visible; "Whole period" shows everything.
// Hovering (or dragging on a phone) reads any match day; hovering a club in
// the leaderboard highlights its line.
//
// Steps are match days (every date with a Premier League match), so each
// result is its own step even when a club plays twice in a week.
//
// Motion: the playhead is fractional, so lines grow smoothly between days
// along monotone curves (no overshoot), the Follow window pans continuously
// and the value axis eases toward its target while playing. At 1x a season
// takes about 25 s (a long range at most 2 minutes).

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Slider, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BRAND } from "../../theme";
import { clubInfo, seasonText } from "./data";
import RaceVideoButton from "./RaceVideoButton";
import {
  boardAt,
  buildSeries,
  fmtDay,
  has,
  linePath,
  placeHeads,
  playSeconds,
  raceWindow,
  scales,
  seasonsIn,
  targetDomain,
  xTicksFor,
  yTicksFor,
} from "./raceGeometry";

// Each selected club's line colour, in order.
export const LINE_COLORS = ["#39d0ff", "#ff4da6", "#ffd23f", "#7cf29a", "#b18cff", "#ff8a3d", "#4dd4c6", "#ff6b6b", "#9ec5ff", "#e6e8ee"];
const SPEEDS = [0.5, 1, 2, 4];
const ROW_H = 34; // leaderboard row

function useWidth(ref, fallback) {
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(ref.current);
    setW(ref.current.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function Toggle({ options, value, onChange, label }) {
  return (
    <Box role="radiogroup" aria-label={label} sx={{ display: "flex", gap: 0.5 }}>
      {options.map(([v, text]) => (
        <Box
          key={v}
          component="button"
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          sx={{
            font: "inherit",
            cursor: "pointer",
            px: 1.1,
            py: 0.45,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            whiteSpace: "nowrap",
            color: value === v ? "#fff" : BRAND.textSecondary,
            background: value === v ? "rgba(111,93,245,0.35)" : "transparent",
            border: `1px solid ${value === v ? "transparent" : "rgba(255,255,255,0.14)"}`,
            "&:focus-visible": { outline: `2px solid ${BRAND.cyan}` },
          }}
        >
          {text}
        </Box>
      ))}
    </Box>
  );
}

const stepButtonSx = {
  width: 36,
  height: 36,
  color: "#e6e8ee",
  border: "1px solid rgba(255,255,255,0.14)",
  "&:hover": { borderColor: BRAND.cyan, background: "rgba(57,208,255,0.08)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.06)" },
};

// `video` ({ range, title, fileName }) names the downloadable video.
export default function RaceChart({ race, teams, selected, colors, metric, autoplay, viewKey, video }) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("md"));
  const wrapRef = useRef(null);
  const width = useWidth(wrapRef, typeof window !== "undefined" ? Math.min(window.innerWidth - 32, 900) : 900);
  const n = race.days.length;
  const [cursor, setCursorState] = useState(n - 1);
  const cursorRef = useRef(n - 1); // the playhead (fractional match days), read by the animation loop
  const setCursor = (v) => {
    cursorRef.current = v;
    setCursorState(v);
  };
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [mode, setMode] = useState("follow");
  const [hover, setHover] = useState(null); // match day under the pointer
  const [focus, setFocus] = useState(null); // highlighted club index

  // A new range, measure or selection shows the finished lines…
  const selKey = selected.join(",");
  useEffect(() => {
    cursorRef.current = n - 1;
    setCursorState(n - 1);
    setPlaying(false);
    setHover(null);
  }, [race, metric.key, selKey, n]);
  // …unless the page asked to play this view (a famous race): start from
  // the first match day once the view it asked for is on screen. The request names its
  // view (the URL's params), because the URL can render before or after it.
  const played = useRef(0);
  useEffect(() => {
    if (!autoplay || autoplay.n === played.current || autoplay.viewKey !== viewKey) return;
    played.current = autoplay.n;
    cursorRef.current = 0;
    setCursorState(0);
    setMode("follow");
    setPlaying(true);
  }, [autoplay, viewKey]);

  const seasonsInRange = seasonsIn(race);
  useEffect(() => {
    if (!playing) return undefined;
    const perSecond = (Math.max(1, n - 1) / playSeconds(race)) * speed;
    let last = performance.now();
    let raf;
    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000); // a background tab doesn't leap ahead
      last = now;
      const next = Math.min(n - 1, cursorRef.current + dt * perSecond);
      cursorRef.current = next;
      setCursorState(next);
      if (next >= n - 1) setPlaying(false);
      else raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, n, race]);

  const series = useMemo(() => buildSeries(race, selected, metric), [race, selected, metric]);
  const view = raceWindow(n, seasonsInRange, cursor, mode);
  const { pos, cur, frac, windowW, xStart, xEnd } = view;

  const H = narrow ? 380 : 540;
  const M = { top: 16, right: narrow ? 96 : 150, bottom: 30, left: metric.key === "pos" ? 30 : 50 };
  const plotW = Math.max(40, width - M.left - M.right);
  const plotH = H - M.top - M.bottom;

  // Value axis: fitted to what's drawn in Follow (eased while playing so it
  // doesn't jump); from zero (or 1st) in Whole period.
  const target = targetDomain(series, metric, mode, view);
  const dom = useRef(null);
  if (playing && dom.current) {
    dom.current = { min: dom.current.min + (target.min - dom.current.min) * 0.12, max: dom.current.max + (target.max - dom.current.max) * 0.12 };
  } else {
    dom.current = target;
  }
  const { x, y } = scales(view, { left: M.left, top: M.top, width: plotW, height: plotH }, dom.current, metric.invert);
  const yTicks = yTicksFor(metric, dom.current);
  const xTicks = useMemo(
    () => xTicksFor(race, { firstW: view.firstW, lastW: view.lastW, xStart, xEnd }, plotW),
    [race, view.firstW, view.lastW, xStart, xEnd, plotW]
  );
  const path = (s) => linePath(s, view, x, y);
  const heads = placeHeads(series, view, x, y, narrow ? 15 : 19, M.top + 6, M.top + plotH);

  // The leaderboard reads the hovered match day, or the playhead.
  const readDay = hover ?? cur;
  const board = boardAt(series, metric, readDay, hover !== null ? 0 : frac);
  const day = race.days[readDay];
  const done = cur >= n - 1;
  const dim = (i) => (focus !== null && focus !== i ? 0.18 : 1);
  const step = (delta) => {
    setPlaying(false);
    setHover(null);
    setCursor(Math.max(0, Math.min(n - 1, Math.round(pos) + delta)));
  };

  const onPointer = (e) => {
    if (playing) return;
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * width;
    const w = Math.round(xStart + ((px - M.left) / plotW) * (xEnd - xStart));
    setHover(Math.max(Math.ceil(xStart), Math.min(cur, w)));
  };
  const clipId = `race-clip-${metric.key}`;

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 2, md: 3 }, alignItems: "stretch" }}>
        <Box ref={wrapRef} sx={{ flex: "1 1 0", minWidth: 0 }}>
          <svg
            width="100%"
            height={H}
            viewBox={`0 0 ${width} ${H}`}
            role="img"
            aria-label={`${metric.label} week by week for ${selected.map((i) => clubInfo(teams[i].team).name).join(", ")}`}
            style={{ display: "block", touchAction: "pan-y" }}
            onPointerMove={onPointer}
            onPointerDown={onPointer}
            onPointerLeave={() => setHover(null)}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={M.left - 2} y={0} width={plotW + 4} height={H} />
              </clipPath>
            </defs>
            {yTicks.map((t) => (
              <g key={t}>
                <line x1={M.left} x2={M.left + plotW} y1={y(t)} y2={y(t)} stroke="rgba(255,255,255,0.07)" />
                <text x={M.left - 8} y={y(t)} fill={BRAND.textSecondary} fontSize="11" textAnchor="end" dominantBaseline="middle" style={{ fontFeatureSettings: '"tnum"' }}>
                  {metric.key === "pos" ? t : metric.format(t)}
                </text>
              </g>
            ))}
            {xTicks.map((t) => (
              <text key={t.w} x={x(t.w)} y={H - 10} fill={BRAND.textSecondary} fontSize="11" textAnchor="middle">
                {t.label}
              </text>
            ))}
            {!done && <line x1={x(pos)} x2={x(pos)} y1={M.top} y2={M.top + plotH} stroke="rgba(255,255,255,0.18)" strokeDasharray="3 4" />}
            <g clipPath={`url(#${clipId})`}>
              {series.map((s, k) => (
                <path
                  key={s.i}
                  d={path(s)}
                  fill="none"
                  stroke={colors[k]}
                  strokeWidth={focus === s.i ? 4 : narrow ? 2 : 2.5}
                  strokeOpacity={dim(s.i)}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
            </g>
            {hover !== null && (
              <g pointerEvents="none">
                <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={M.top + plotH} stroke="rgba(255,255,255,0.45)" />
                {series.map((s, k) =>
                  has(s.vals[hover]) ? <circle key={s.i} cx={x(hover)} cy={y(s.vals[hover])} r="4.5" fill={colors[k]} stroke={BRAND.bg} strokeWidth="2" opacity={dim(s.i)} /> : null
                )}
              </g>
            )}
            {heads.map((h) => {
              const t = teams[h.i];
              const r = narrow ? 9 : 11;
              return (
                <g key={h.i} opacity={dim(h.i)}>
                  {Math.abs(h.ly - h.hy) > 2 && <line x1={h.hx + r} y1={h.hy} x2={h.hx + r + 6} y2={h.ly} stroke={colors[h.k]} strokeOpacity="0.5" />}
                  <circle cx={h.hx} cy={h.hy} r={r + 2} fill={BRAND.bg} stroke={colors[h.k]} strokeWidth="2" />
                  {t.image && <image href={t.image} x={h.hx - r} y={h.hy - r} width={r * 2} height={r * 2} preserveAspectRatio="xMidYMid meet" />}
                  <text x={h.hx + r + 8} y={h.ly} fill="#fff" fontSize={narrow ? 10.5 : 12.5} fontWeight="700" dominantBaseline="middle">
                    {clubInfo(t.team).short}
                    {/* Phones: names only; the leaderboard below has the values. */}
                    {!narrow && <tspan fill={colors[h.k]} dx="5">{metric.format(h.v)}</tspan>}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>

        <Box sx={{ width: { xs: "100%", md: 240 }, flexShrink: 0 }}>
          <Typography sx={{ color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", mb: 1 }}>
            {metric.label}, {day ? fmtDay(day.date) : ""}
          </Typography>
          <Box sx={{ position: "relative", height: board.length * ROW_H }} onMouseLeave={() => setFocus(null)}>
            {board.map((r, rank) => {
              const t = teams[r.i];
              return (
                <Box
                  key={r.i}
                  onMouseEnter={() => setFocus(r.i)}
                  onClick={() => setFocus((f) => (f === r.i ? null : r.i))}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    height: ROW_H - 4,
                    transform: `translateY(${rank * ROW_H}px)`,
                    transition: "transform 380ms cubic-bezier(0.22, 1, 0.36, 1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    opacity: dim(r.i),
                    background: focus === r.i ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,0.035)",
                    borderLeft: `3px solid ${colors[r.k]}`,
                    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                  }}
                >
                  <Typography sx={{ color: BRAND.textSecondary, fontSize: 12, width: 16, fontFeatureSettings: '"tnum"' }}>{rank + 1}</Typography>
                  {t.image && <Box component="img" src={t.image} alt="" sx={{ width: 18, height: 18, objectFit: "contain" }} />}
                  <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {clubInfo(t.team).short}
                  </Typography>
                  <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 800, fontFeatureSettings: '"tnum"' }}>{metric.format(r.v)}</Typography>
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ color: BRAND.textSecondary, fontSize: 12, mt: 1.5, lineHeight: 1.5 }}>
            {narrow ? "Drag across the chart to read any match day. Tap a club to highlight it." : "Hover the chart to read any match day, or a club to highlight its line."}
          </Typography>
        </Box>
      </Box>

      {/* Playback and view */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, mt: 2, flexWrap: "wrap" }}>
        <IconButton onClick={() => step(-1)} disabled={pos <= 0} aria-label="Previous match day" title="Previous match day" sx={stepButtonSx}>
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => {
            if (playing) setPlaying(false);
            else {
              if (done) setCursor(0);
              setHover(null);
              setPlaying(true);
            }
          }}
          aria-label={playing ? "Pause" : done ? "Replay from the start" : "Play"}
          sx={{ width: 44, height: 44, color: "#fff", background: BRAND.gradient, "&:hover": { background: BRAND.gradientReverse } }}
        >
          {playing ? <PauseIcon /> : done ? <ReplayIcon /> : <PlayArrowIcon />}
        </IconButton>
        <IconButton onClick={() => step(1)} disabled={done} aria-label="Next match day" title="Next match day" sx={stepButtonSx}>
          <ChevronRightIcon />
        </IconButton>
        <Slider
          value={pos}
          min={0}
          max={Math.max(0, n - 1)}
          step={1}
          onChange={(_, v) => {
            setPlaying(false);
            setHover(null);
            setCursor(v);
          }}
          aria-label="Match day"
          getAriaValueText={(v) => (race.days[Math.round(v)] ? fmtDay(race.days[Math.round(v)].date) : "")}
          sx={{ flex: 1, minWidth: 160, color: BRAND.cyan, mx: { xs: 1, sm: 0.5 }, "& .MuiSlider-thumb, & .MuiSlider-track": { transition: "none" } }}
        />
        <Toggle label="Playback speed" options={SPEEDS.map((sp) => [sp, `${sp}×`])} value={speed} onChange={setSpeed} />
        {windowW < n && (
          <Toggle label="View" options={[["follow", "Follow the race"], ["full", "Whole period"]]} value={mode} onChange={setMode} />
        )}
        <Typography sx={{ color: BRAND.textSecondary, fontSize: 12.5, fontFeatureSettings: '"tnum"', whiteSpace: "nowrap" }}>
          {race.days[cur] ? `${seasonText(race.days[cur].season)} · ${fmtDay(race.days[cur].date)}` : ""}
        </Typography>
      </Box>
      {video && (
        <RaceVideoButton
          race={race}
          teams={teams}
          selected={selected}
          colors={colors}
          metric={metric}
          mode={mode}
          speed={speed}
          {...video}
          onOpen={() => setPlaying(false)}
        />
      )}
    </Box>
  );
}
